import React, { useState } from 'react';
import './MessageBubble.css';

interface Props {
  role: 'user' | 'assistant';
  content: string;
  progress?: { current: number; total: number; percent: number };
}

// 간단한 마크다운 파싱 (bold, bullet)
const parseMarkdown = (text: string): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  // **bold** 패턴 처리
  const boldRegex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match;

  while ((match = boldRegex.exec(text)) !== null) {
    // 매치 전 텍스트
    if (match.index > lastIndex) {
      parts.push(<span key={key++}>{text.slice(lastIndex, match.index)}</span>);
    }
    // bold 텍스트
    parts.push(<strong key={key++}>{match[1]}</strong>);
    lastIndex = match.index + match[0].length;
  }

  // 나머지 텍스트
  if (lastIndex < text.length) {
    parts.push(<span key={key++}>{text.slice(lastIndex)}</span>);
  }

  return parts.length > 0 ? parts : [text];
};

const MessageBubble: React.FC<Props> = ({ role, content }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // 줄 렌더링 (마크다운 지원)
  const renderLine = (line: string, index: number, total: number) => {
    // 불릿 포인트 (• 로 시작)
    const isBullet = line.startsWith('• ');
    const lineContent = isBullet ? line.slice(2) : line;

    return (
      <React.Fragment key={index}>
        {isBullet && <span className="bullet-point">• </span>}
        {parseMarkdown(lineContent)}
        {index < total - 1 && <br />}
      </React.Fragment>
    );
  };

  return (
    <div className={`message-row ${role}`}>
      {role === 'user' ? (
        <div className="user-bubble">
          <div className="user-bubble-inner">
            {content.split('\n').map((line, i, arr) => renderLine(line, i, arr.length))}
          </div>
        </div>
      ) : (
        <div className="assistant-message">
          <div className="assistant-content">
            {content.split('\n').map((line, i, arr) => renderLine(line, i, arr.length))}
          </div>
          <div className="message-actions">
            <button className="action-btn" onClick={handleCopy} title="복사">
              {copied ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
              )}
            </button>
            <button className="action-btn" title="좋아요">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
              </svg>
            </button>
            <button className="action-btn" title="싫어요">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"></path>
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageBubble;
